// src/hooks/useBlueBiMutations.ts
// Mutações do módulo Blue B.I. (issue #42). Molde: `useLojaMutations` — guard síncrono
// contra duplo disparo, Toast no caminho feliz, leitura de `.error` com fallback para
// `.message`, e `onReload` após sucesso (premissa #4).
//
// ⚠️ O `link` do painel é uma URL "Publish to web" anônima e permanente: ele viaja no
// CORPO do POST (nunca em query string, logo nunca no log de URL do interceptor) e não
// é registrado em `logger.*` em nenhum caminho, nem no de erro.
import { useRef, useState } from 'react';
import axios from 'axios';
import { apiDelete, apiGet, apiPost, apiPut } from '../services/api';
import { showAlert, showConfirm } from '../contexts/DialogContext';
import { Toast } from '../contexts/ToastContext';

interface Options {
  onReload: () => void;
}

export interface NovoPainel {
  rotulo: string;
  link: string;
  usuarioIds: number[];
}

export const useBlueBiMutations = ({ onReload }: Options) => {
  const [isSaving, setIsSaving] = useState(false);
  // Guard síncrono: impede um segundo envio enquanto o primeiro está em voo (a ref muda
  // na hora, sem esperar re-render). Mesmo padrão de `useLojaMutations.handleToggleAtivo`.
  const salvandoRef = useRef(false);

  /**
   * Cadastra um painel e concede os vínculos escolhidos. Devolve `true` quando o painel
   * foi criado — é o sinal para o formulário limpar e fechar.
   *
   * Os vínculos vão em SÉRIE, um POST por usuário: não existe rota de concessão em lote,
   * e o backend documenta que a idempotência da concessão não vale sob concorrência
   * (duas chamadas simultâneas com o mesmo par: a segunda esbarra na unicidade e toma 400).
   */
  const criarPainel = async ({ rotulo, link, usuarioIds }: NovoPainel): Promise<boolean> => {
    if (salvandoRef.current) return false;
    salvandoRef.current = true;
    setIsSaving(true);
    try {
      const response = await apiPost('/v1/bluebi/gestao/paineis', {
        rotulo: rotulo.trim(),
        link: link.trim(),
      });
      const painelId: number | undefined = response.data?.id;
      if (!painelId) {
        await showAlert('O painel foi criado, mas a resposta veio sem identificador. Recarregue a tela.', {
          variant: 'warning',
        });
        onReload();
        return true;
      }

      const falhas: number[] = [];
      for (const usuarioId of usuarioIds) {
        try {
          await apiPost(`/v1/bluebi/gestao/paineis/${painelId}/usuarios`, { usuarioId });
        } catch {
          falhas.push(usuarioId);
        }
      }

      if (falhas.length === 0) {
        Toast.success('Painel cadastrado com sucesso!');
      } else {
        // O painel EXISTE — não há rota para apagá-lo, e desativá-lo em silêncio esconderia
        // um cadastro que deu certo. O caminho honesto é avisar o que faltou e deixar o
        // usuário completar os acessos pela edição do painel.
        await showAlert(
          `Painel cadastrado, mas ${falhas.length} de ${usuarioIds.length} acesso(s) não foram concedidos. ` +
            'Edite o painel para concedê-los novamente.',
          { variant: 'warning' }
        );
      }
      onReload();
      return true;
    } catch (err) {
      // Contrato de erro destas rotas: `{ error, code }`. O javadoc do backend garante que a
      // mensagem NUNCA ecoa o link recebido, então exibi-la é seguro e diz qual validação
      // falhou. O erro cru não vai para `logger` — ele carrega config.headers com o Bearer.
      const data = axios.isAxiosError(err)
        ? (err.response?.data as { error?: string; message?: string })
        : undefined;
      await showAlert(data?.error ?? data?.message ?? 'Erro ao cadastrar o painel', { variant: 'error' });
      return false;
    } finally {
      setIsSaving(false);
      salvandoRef.current = false;
    }
  };

  /**
   * Altera rótulo e link e acerta os vínculos para EXACTAMENTE a lista escolhida.
   *
   * Não existe rota de substituição em lote de vínculos, então a sincronização é um diff
   * contra o que o backend tem hoje: um POST por inclusão, um DELETE por remoção, em série.
   * A revogação é idempotente quanto ao vínculo (204 mesmo se ele já não existia), mas não
   * quanto ao usuário — um id que não existe no tenant é recusado com 400.
   */
  const atualizarPainel = async (
    painelId: number,
    { rotulo, link, usuarioIds }: NovoPainel
  ): Promise<boolean> => {
    if (salvandoRef.current) return false;
    salvandoRef.current = true;
    setIsSaving(true);
    try {
      await apiPut(`/v1/bluebi/gestao/paineis/${painelId}`, {
        rotulo: rotulo.trim(),
        link: link.trim(),
      });

      const atuais: number[] = (await apiGet(`/v1/bluebi/gestao/paineis/${painelId}/usuarios`)).data
        ?.map((v: { usuarioId: number }) => v.usuarioId) ?? [];
      const incluir = usuarioIds.filter((id) => !atuais.includes(id));
      const remover = atuais.filter((id) => !usuarioIds.includes(id));

      const falhas: number[] = [];
      for (const usuarioId of incluir) {
        try {
          await apiPost(`/v1/bluebi/gestao/paineis/${painelId}/usuarios`, { usuarioId });
        } catch {
          falhas.push(usuarioId);
        }
      }
      for (const usuarioId of remover) {
        try {
          await apiDelete(`/v1/bluebi/gestao/paineis/${painelId}/usuarios/${usuarioId}`);
        } catch {
          falhas.push(usuarioId);
        }
      }

      if (falhas.length === 0) {
        Toast.success('Painel atualizado com sucesso!');
      } else {
        await showAlert(
          `Painel atualizado, mas ${falhas.length} acesso(s) não foram alterados. Abra o painel e tente novamente.`,
          { variant: 'warning' }
        );
      }
      onReload();
      return true;
    } catch (err) {
      const data = axios.isAxiosError(err)
        ? (err.response?.data as { error?: string; message?: string })
        : undefined;
      await showAlert(data?.error ?? data?.message ?? 'Erro ao atualizar o painel', { variant: 'error' });
      return false;
    } finally {
      setIsSaving(false);
      salvandoRef.current = false;
    }
  };

  /**
   * Tira o painel de circulação, ou o devolve a ela. Painel nunca é apagado: os vínculos
   * permanecem e voltam a valer na reativação.
   *
   * ⚠️ Desativar NÃO revoga o link — quem já copiou a URL continua abrindo o relatório. A
   * confirmação diz isso na hora de decidir, não em documentação.
   */
  const alternarAtivo = async (painelId: number, ativar: boolean): Promise<boolean> => {
    if (salvandoRef.current) return false;
    // O guard é marcado ANTES de perguntar: dois cliques rápidos empilhariam dois diálogos
    // de confirmação para uma única ação. Liberado no cancelamento e no `finally`.
    salvandoRef.current = true;
    // A confirmação de desativar repete a contenção no momento da decisão: quem desativa
    // precisa saber ali que isso NÃO fecha o acesso de quem já tem a URL.
    if (!ativar) {
      const confirmado = await showConfirm(
        'Desativar tira o painel da tela dos usuários, mas NÃO revoga o link: quem já o copiou ' +
          'continua abrindo o relatório. A revogação só existe no próprio Power B.I. Deseja continuar?',
        { variant: 'warning', title: 'Desativar painel' }
      );
      if (!confirmado) {
        salvandoRef.current = false;
        return false;
      }
    }
    setIsSaving(true);
    try {
      const acao = ativar ? 'reativar' : 'desativar';
      await apiPut(`/v1/bluebi/gestao/paineis/${painelId}/${acao}`);
      Toast.success(ativar ? 'Painel reativado com sucesso!' : 'Painel desativado com sucesso!');
      onReload();
      return true;
    } catch (err) {
      const data = axios.isAxiosError(err)
        ? (err.response?.data as { error?: string; message?: string })
        : undefined;
      await showAlert(data?.error ?? data?.message ?? 'Erro ao alterar o painel', { variant: 'error' });
      return false;
    } finally {
      setIsSaving(false);
      salvandoRef.current = false;
    }
  };

  return { isSaving, criarPainel, atualizarPainel, alternarAtivo };
};
